import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Warning, Lightning, Package, ArrowsClockwise, Info, ClockCountdown } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Backorders() {
  const { isAdmin } = useAuth();
  const { company, refresh } = useCompany();
  const [data, setData] = useState({ orders: [], rollup: [], count: 0 });
  const [recs, setRecs] = useState({ recommendations: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [applyingRec, setApplyingRec] = useState(null);
  const [applyingAll, setApplyingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bo, rc] = await Promise.all([
        axios.get(`${API}/api/backorders`, { withCredentials: true }),
        axios.get(`${API}/api/backorders/reorder-recommendations`, { withCredentials: true }),
      ]);
      setData(bo.data || { orders: [], rollup: [], count: 0 });
      setRecs(rc.data || { recommendations: [], count: 0 });
    } catch (e) {
      toast.error('Failed to load backorders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const release = async (orderId) => {
    setBusy(orderId);
    try {
      const r = await axios.post(`${API}/api/orders/${orderId}/release-backorder`, {}, { withCredentials: true });
      const totalReleased = (r.data?.released_lines || []).reduce((s, l) => s + (l.released || 0), 0);
      if (totalReleased > 0) {
        toast.success(`Released ${totalReleased} unit${totalReleased !== 1 ? 's' : ''} · status now ${r.data?.status}`);
      } else {
        toast.message('No stock available to release for this order yet.');
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Release failed');
    } finally {
      setBusy(null);
    }
  };

  // PATCH ETA on a backordered line. Optimistic: update local state first
  // so the input reflects instantly, then sync to backend; revert on error.
  const saveEta = async (orderId, variantId, eta) => {
    const cleanEta = eta || null;
    // Optimistic update
    setData((prev) => ({
      ...prev,
      orders: prev.orders.map((o) => {
        if (o.id !== orderId) return o;
        return {
          ...o,
          items: (o.items || []).map((it) =>
            it.variant_id === variantId ? { ...it, eta: cleanEta } : it),
        };
      }),
    }));
    try {
      await axios.patch(
        `${API}/api/orders/${orderId}/backorder-eta`,
        { variant_id: variantId, eta: cleanEta },
        { withCredentials: true },
      );
      toast.success(cleanEta ? `ETA set to ${cleanEta}` : 'ETA cleared');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save ETA');
      // Pull fresh state on failure
      load();
    }
  };

  const toggleAllowBackorders = async (next) => {
    setSavingToggle(true);
    try {
      await axios.patch(`${API}/api/companies/me/settings/backorders`, { allow_backorders: next }, { withCredentials: true });
      await refresh();
      toast.success(`Backorders ${next ? 'enabled' : 'disabled'} company-wide`);
    } catch (e) {
      toast.error('Failed to update setting');
    } finally {
      setSavingToggle(false);
    }
  };

  const promoteToDrafts = async () => {
    setPromoting(true);
    try {
      const r = await axios.post(`${API}/api/backorders/promote-to-draft-pos`, {}, { withCredentials: true });
      const created = r.data?.created || 0;
      const skipped = r.data?.skipped || 0;
      if (created === 0 && skipped === 0) {
        toast.message('No backorders to promote.');
      } else if (created === 0) {
        toast.message(`No new drafts — ${skipped} variant${skipped !== 1 ? 's' : ''} already have pending drafts.`);
      } else {
        toast.success(
          `Created ${created} draft PO${created !== 1 ? 's' : ''}${skipped ? ` · ${skipped} skipped (already pending)` : ''}. Open Manufacturing → Vendor Consolidation to review.`,
          { duration: 6000 }
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create draft POs');
    } finally {
      setPromoting(false);
    }
  };

  const applyRec = async (rec) => {
    setApplyingRec(rec.sku_id);
    try {
      await axios.post(
        `${API}/api/backorders/reorder-recommendations/${rec.sku_id}/apply`,
        { recommended_reorder_point: rec.recommended_reorder_point },
        { withCredentials: true }
      );
      toast.success(`${rec.sku_code} reorder point: ${rec.current_reorder_point} → ${rec.recommended_reorder_point}`);
      // Optimistic remove from list
      setRecs((r) => ({ ...r, recommendations: r.recommendations.filter((x) => x.sku_id !== rec.sku_id) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplyingRec(null);
    }
  };

  const applyAllRecs = async () => {
    if (recs.recommendations.length === 0) return;
    setApplyingAll(true);
    try {
      const rows = recs.recommendations.map((r) => ({
        sku_id: r.sku_id,
        recommended_reorder_point: r.recommended_reorder_point,
      }));
      const r = await axios.post(
        `${API}/api/backorders/reorder-recommendations/apply-all`,
        { rows },
        { withCredentials: true }
      );
      const applied = r.data?.applied || 0;
      const errors = (r.data?.errors || []).length;
      if (applied > 0) {
        toast.success(`Applied ${applied} reorder-point bump${applied !== 1 ? 's' : ''}${errors ? ` · ${errors} failed` : ''}`);
      } else {
        toast.message('No bumps applied.');
      }
      setRecs({ recommendations: [], count: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk apply failed');
    } finally {
      setApplyingAll(false);
    }
  };

  const overdueSummary = data?.overdue_summary || { overdue_orders: 0, overdue_lines: 0 };

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6" data-testid="backorders-page">
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Warning size={26} weight="fill" className="text-rose-600" /> Backorders
            </h1>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
              Orders waiting on stock · auto-release FIFO when goods are received
            </p>
          </div>
          <Link
            to="/orders"
            className="text-xs uppercase tracking-widest font-bold text-brand hover:underline"
            data-testid="backorders-back-to-orders"
          >
            ← All orders
          </Link>
        </header>

        {/* Top-of-page overdue alert — only shown when at least one line has
            slipped its promised ETA. Loud rose tile so warehouse managers
            can't miss it on the daily glance. */}
        {overdueSummary.overdue_lines > 0 && (
          <div
            data-testid="backorders-overdue-alert"
            className="border-2 border-rose-300 bg-rose-50 p-4 flex items-start gap-3 flex-wrap"
          >
            <Warning size={22} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[280px]">
              <div className="text-sm font-bold text-rose-900">
                {overdueSummary.overdue_lines} backorder line{overdueSummary.overdue_lines !== 1 ? 's' : ''} past promised ETA
              </div>
              <p className="text-xs text-rose-800 mt-1">
                Across {overdueSummary.overdue_orders} order{overdueSummary.overdue_orders !== 1 ? 's' : ''}.
                Chase the supplier or update the ETA inline below — ops gets a digest of these every morning at 09:00 UTC.
              </p>
            </div>
          </div>
        )}

        {/* Admin toggle */}
        {isAdmin && company && (
          <div className="border border-border bg-card p-4 flex items-start gap-3 flex-wrap" data-testid="backorders-allow-toggle-card">
            <Info size={18} weight="fill" className="text-brand shrink-0 mt-0.5" />
            <div className="flex-1 min-w-[280px]">
              <div className="text-sm font-bold">Allow backorders</div>
              <p className="text-xs text-muted-foreground mt-1">
                When enabled (default), orders with insufficient stock are accepted and the short qty is tracked
                as a backorder. When disabled, the API rejects short-stock orders with a 422 listing the missing SKUs.
              </p>
            </div>
            <Switch
              checked={!!company.allow_backorders}
              disabled={savingToggle}
              onCheckedChange={toggleAllowBackorders}
              data-testid="backorders-allow-toggle"
            />
          </div>
        )}

        {/* Reorder Recommendations (demand-aware) */}
        <section data-testid="backorders-reco-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h2 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                <Lightning size={16} weight="fill" className="text-emerald-600" /> Reorder recommendations
              </h2>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Last 90 days · bump reorder points for SKUs that stocked out
              </p>
            </div>
            {recs.recommendations.length > 0 && (
              <Button
                onClick={applyAllRecs}
                disabled={applyingAll}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none font-bold uppercase tracking-widest text-xs"
                data-testid="reco-apply-all"
              >
                <Lightning size={12} weight="fill" className="mr-1" />
                {applyingAll ? 'Applying…' : `Apply all (${recs.recommendations.length})`}
              </Button>
            )}
          </div>
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : recs.recommendations.length === 0 ? (
            <div className="text-sm text-muted-foreground border border-dashed border-border p-4" data-testid="reco-empty">
              No recommendations — your reorder points are keeping up with demand.
            </div>
          ) : (
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-sm" data-testid="reco-table">
                <thead className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border bg-muted">
                  <tr>
                    <th className="text-left px-3 py-2">SKU</th>
                    <th className="text-right px-3 py-2">Incidents (90d)</th>
                    <th className="text-right px-3 py-2">Total short</th>
                    <th className="text-right px-3 py-2">Avg / incident</th>
                    <th className="text-right px-3 py-2">Current</th>
                    <th className="text-right px-3 py-2">Recommended</th>
                    <th className="text-right px-3 py-2">Δ</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {recs.recommendations.map((r) => (
                    <tr key={r.sku_id} className="border-b border-border" data-testid={`reco-row-${r.sku_id}`}>
                      <td className="px-3 py-2 font-mono">{r.sku_code || r.sku_id?.slice(0, 8)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.incidents}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-rose-600 font-bold">{r.total_short}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.avg_short_per_incident}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-mono">{r.current_reorder_point}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-mono font-bold text-emerald-700">{r.recommended_reorder_point}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">+{r.delta}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => applyRec(r)}
                          disabled={applyingRec === r.sku_id}
                          className="rounded-none text-[11px] font-bold uppercase tracking-widest"
                          data-testid={`reco-apply-${r.sku_id}`}
                        >
                          {applyingRec === r.sku_id ? 'Applying…' : 'Apply'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Per-SKU rollup */}
        <section data-testid="backorders-rollup-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
              <Package size={16} weight="fill" className="text-brand" /> Demand by SKU
            </h2>
            {data.rollup.length > 0 && (
              <Button
                onClick={promoteToDrafts}
                disabled={promoting}
                className="bg-brand hover:bg-brand/90 text-white rounded-none font-bold uppercase tracking-widest text-xs"
                data-testid="backorders-promote-drafts"
              >
                <Lightning size={12} weight="fill" className="mr-1" />
                {promoting ? 'Creating drafts…' : 'Create draft POs'}
              </Button>
            )}
          </div>
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : data.rollup.length === 0 ? (
            <div className="text-sm text-muted-foreground border border-dashed border-border p-4">
              No open backorders 🎉 — every order is fully fulfilled.
            </div>
          ) : (
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-sm" data-testid="backorders-rollup-table">
                <thead className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border bg-muted">
                  <tr>
                    <th className="text-left px-3 py-2">SKU</th>
                    <th className="text-right px-3 py-2">Qty short</th>
                    <th className="text-right px-3 py-2">Orders affected</th>
                    <th className="text-right px-3 py-2">Available now</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rollup.map((r) => {
                    const ready = (r.available_now || 0) >= r.qty_short;
                    const partial = !ready && (r.available_now || 0) > 0;
                    return (
                      <tr key={r.variant_id} className="border-b border-border" data-testid={`backorders-rollup-row-${r.variant_id}`}>
                        <td className="px-3 py-2 font-mono">{r.sku_code || r.variant_id?.slice(0, 8)}</td>
                        <td className="px-3 py-2 text-right font-bold tabular-nums text-rose-600">{r.qty_short}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.orders_affected}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-mono">{r.available_now}</td>
                        <td className="px-3 py-2">
                          {ready ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 px-2 py-0.5">
                              <Lightning size={10} weight="fill" /> Ready to release
                            </span>
                          ) : partial ? (
                            <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700 bg-amber-50 px-2 py-0.5">
                              Partial · {r.available_now} of {r.qty_short}
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground bg-muted px-2 py-0.5">
                              Awaiting receive
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Open backorder orders */}
        <section data-testid="backorders-orders-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
              <Warning size={16} weight="fill" className="text-rose-600" /> Open backordered orders ({data.count})
            </h2>
            <Button
              variant="outline"
              onClick={load}
              className="rounded-none"
              data-testid="backorders-refresh"
            >
              <ArrowsClockwise size={14} weight="bold" className="mr-1" /> Refresh
            </Button>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : data.orders.length === 0 ? (
            <div className="text-sm text-muted-foreground border border-dashed border-border p-4">
              No backordered orders.
            </div>
          ) : (
            <div className="space-y-3">
              {data.orders.map((o) => {
                const lines = (o.items || []).filter((it) => Number(it.backorder_qty || 0) > 0);
                const totalShort = lines.reduce((s, it) => s + Number(it.backorder_qty || 0), 0);
                return (
                  <div key={o.id} className="border border-border p-4 bg-card" data-testid={`backorder-order-${o.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="font-mono text-sm font-bold">{o.order_number}</div>
                        <div className="text-xs text-muted-foreground">
                          {o.customer_name || '—'} · created {(o.created_at || '').slice(0, 10)}
                        </div>
                        <span
                          className={`inline-block mt-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                            o.status === 'backordered' ? 'bg-rose-100 text-rose-800' : 'bg-orange-100 text-orange-800'
                          }`}
                          data-testid={`backorder-order-status-${o.id}`}
                        >
                          {o.status}
                        </span>
                        {Number(o.overdue_lines_count || 0) > 0 && (
                          <span
                            data-testid={`backorder-order-overdue-chip-${o.id}`}
                            title={`${o.overdue_lines_count} line(s) past promised ETA · oldest is ${o.oldest_overdue_days} day(s) late`}
                            className="ml-2 inline-flex items-center gap-1 mt-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-rose-600 text-white"
                          >
                            <Warning size={10} weight="fill" /> ETA Overdue · {o.oldest_overdue_days}d
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold tabular-nums text-rose-600">{totalShort}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">units short</div>
                      </div>
                    </div>
                    <div className="mt-3 border-t border-border pt-3 space-y-2">
                      {lines.map((it, i) => (
                        <div key={i} className="text-xs flex items-center justify-between gap-3 flex-wrap" data-testid={`backorder-line-${o.id}-${it.variant_id}`}>
                          <span className="font-mono flex items-center gap-1.5">
                            {it.sku_code || it.variant_id?.slice(0, 8) || '—'}
                            {it.eta_overdue && (
                              <span
                                data-testid={`backorder-line-overdue-${o.id}-${it.variant_id}`}
                                title={`ETA ${it.eta} was ${it.eta_overdue_days} day(s) ago`}
                                className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] uppercase tracking-widest font-bold bg-rose-100 text-rose-800 border border-rose-200"
                              >
                                <Warning size={9} weight="fill" />
                                {it.eta_overdue_days}d late
                              </span>
                            )}
                          </span>
                          <span className="text-muted-foreground flex-1">
                            <span className="text-foreground tabular-nums">{Number(it.fulfilled_qty || 0)}</span>
                            {' / '}
                            <span className="text-foreground tabular-nums">{Number(it.fulfilled_qty || 0) + Number(it.backorder_qty || 0)}</span>
                            {' fulfilled · '}
                            <span className="text-rose-600 font-bold tabular-nums">{Number(it.backorder_qty || 0)}</span>
                            {' short'}
                          </span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <ClockCountdown size={12} weight="bold" className="text-amber-700" />
                            <span className="text-[10px] uppercase tracking-widest font-bold text-amber-800">ETA</span>
                            <Input
                              key={`eta-${o.id}-${it.variant_id}-${it.eta || 'none'}`}
                              type="date"
                              defaultValue={it.eta || ''}
                              min={new Date().toISOString().slice(0, 10)}
                              onBlur={(e) => {
                                const v = e.target.value || '';
                                if (v !== (it.eta || '')) saveEta(o.id, it.variant_id, v);
                              }}
                              data-testid={`backorder-line-eta-${o.id}-${it.variant_id}`}
                              className="h-7 py-0.5 px-2 text-[11px] max-w-[140px]"
                            />
                            {it.eta && (
                              <button
                                type="button"
                                onClick={() => saveEta(o.id, it.variant_id, '')}
                                data-testid={`backorder-line-eta-clear-${o.id}-${it.variant_id}`}
                                className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
                                title="Clear ETA"
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button
                        onClick={() => release(o.id)}
                        disabled={busy === o.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none font-bold uppercase tracking-widest text-xs"
                        data-testid={`backorder-release-${o.id}`}
                      >
                        <Lightning size={12} weight="fill" className="mr-1" />
                        {busy === o.id ? 'Releasing…' : 'Release available stock'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
